import React from 'react';
import { styled } from 'baseui';
import { Select } from 'baseui/select';
import { Button, KIND, SIZE } from 'baseui/button';
import { Flex } from './Flex';
import { BaseDatePicker } from './BaseDatePicker';

// Type assertions for BaseUI compatibility
const SelectComponent = Select as any;

const FiltersContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  flex: 1,
});

const FilterGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  minWidth: '140px',
});

export interface FilterState {
  dateRange: Date[];
  status: string[];
  destination: string[];
  type: string[];
}

interface InlineFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  statusOptions: Array<{ label: string; id: string }>;
  destinationOptions: Array<{ label: string; id: string }>;
  typeOptions: Array<{ label: string; id: string }>;
}

export const InlineFilters: React.FC<InlineFiltersProps> = ({
  filters,
  onFiltersChange,
  statusOptions,
  destinationOptions,
  typeOptions,
}) => {
  const handleDateRangeChange = ({ date }: any) => {
    const newDateRange = Array.isArray(date) ? [...date] : [];
    const newFilters = { ...filters, dateRange: newDateRange };
    onFiltersChange(newFilters);
  };

  const handleStatusChange = (params: any) => {
    const selectedStatus = params.value.map((item: any) => item.id);
    const newFilters = { ...filters, status: selectedStatus };
    onFiltersChange(newFilters);
  };

  const handleDestinationChange = (params: any) => {
    const selectedDestinations = params.value.map((item: any) => item.id);
    const newFilters = { ...filters, destination: selectedDestinations };
    onFiltersChange(newFilters);
  };

  const handleTypeChange = (params: any) => {
    const selectedTypes = params.value.map((item: any) => item.id);
    const newFilters = { ...filters, type: selectedTypes };
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: FilterState = {
      dateRange: [],
      status: [],
      destination: [],
      type: [],
    };
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = filters.dateRange.length > 0 || filters.status.length > 0 || filters.destination.length > 0 || filters.type.length > 0;

  return (
    <FiltersContainer>
      <FilterGroup>
        <BaseDatePicker
          mode="range"
          rangeValue={{
            from: filters.dateRange[0] || undefined,
            to: filters.dateRange[1] || undefined,
          }}
          onRangeChange={(range) => {
            const newDateRange = range?.from && range?.to ? [range.from, range.to] : [];
            const newFilters = { ...filters, dateRange: newDateRange };
            onFiltersChange(newFilters);
          }}
          placeholder="Select dates"
        />
      </FilterGroup>

      <FilterGroup>
        <SelectComponent
          options={typeOptions}
          value={typeOptions.filter(option => filters.type.includes(option.id)) as any}
          onChange={handleTypeChange}
          multi
          placeholder="All types"
          clearable
          searchable={false}
          size="compact"
          overrides={{
            ControlContainer: {
              style: {
                backgroundColor: '#fff',
                borderColor: '#D1D5DB',
                borderRadius: '6px',
                height: '48px',
                minHeight: '48px',
                ':hover': {
                  borderColor: '#9CA3AF',
                },
                ':focus-within': {
                  borderColor: '#000',
                  boxShadow: '0 0 0 1px #000',
                }
              }
            },
            Placeholder: {
              style: {
                fontSize: '13px',
                color: '#9CA3AF',
              }
            }
          }}
        />
      </FilterGroup>

      <FilterGroup>
        <SelectComponent
          options={statusOptions}
          value={statusOptions.filter(option => filters.status.includes(option.id)) as any}
          onChange={handleStatusChange}
          multi
          placeholder="All statuses"
          clearable
          searchable={false}
          size="compact"
          overrides={{
            ControlContainer: {
              style: {
                backgroundColor: '#fff',
                borderColor: '#D1D5DB',
                borderRadius: '6px',
                height: '48px',
                minHeight: '48px',
                ':hover': {
                  borderColor: '#9CA3AF',
                },
                ':focus-within': {
                  borderColor: '#000',
                  boxShadow: '0 0 0 1px #000',
                }
              }
            },
            Placeholder: {
              style: {
                fontSize: '13px',
                color: '#9CA3AF',
              }
            }
          }}
        />
      </FilterGroup>

      <FilterGroup>
        <SelectComponent
          options={destinationOptions}
          value={destinationOptions.filter(option => 
            filters.destination.includes(option.id)
          ) as any}
          onChange={handleDestinationChange}
          multi
          placeholder="All destinations"
          clearable
          searchable
          size="compact"
          overrides={{
            ControlContainer: {
              style: {
                backgroundColor: '#fff',
                borderColor: '#D1D5DB',
                borderRadius: '6px',
                height: '48px',
                minHeight: '48px',
                ':hover': {
                  borderColor: '#9CA3AF',
                },
                ':focus-within': {
                  borderColor: '#000',
                  boxShadow: '0 0 0 1px #000',
                }
              }
            },
            Placeholder: {
              style: {
                fontSize: '13px',
                color: '#9CA3AF',
              }
            },
            Option: {
              style: ({ $isHighlighted }: any) => {
                return {
                  fontSize: '13px',
                  backgroundColor: $isHighlighted ? '#F3F4F6' : 'transparent',
                };
              }
            }
          }}
        />
      </FilterGroup>

      {hasActiveFilters && (
        <div>
          <Button
            size={SIZE.compact}
            kind={KIND.secondary}
            onClick={handleClearFilters}
            overrides={{
              BaseButton: {
                style: {
                  fontSize: '12px',
                  height: '48px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                }
              }
            }}
          >
            Clear
          </Button>
        </div>
      )}
    </FiltersContainer>
  );
}; 